import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Plus, Save, Trash2 } from 'lucide-react';
import { STATION_MODE_LABEL, STATION_MODES, WEEKDAYS, hhmmToMinutes, minutesToHHMM, type Permission, type StationMode } from '@garlic/shared';
import { api, errorMessage, fieldErrors, patch, post, put } from '../../lib/api';
import { Alert, Button, Card, CardHeader, Field, Input, PageHeader, Spinner, Textarea, cx } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { Switch } from '../../ui/controls';

type StationRow = { id: string; name: string; kind: 'KITCHEN' | 'BAR' | 'OTHER'; defaultPrepMinutes: number; mode: StationMode; printers: number };
type Settings = {
  settings: {
    primaryColor: string; accentColor: string; logoPath: string | null; googleReviewUrl: string | null; acceptOrders: boolean; enforceHours: boolean; autoOpenSessions: boolean; menuNotice: string | null;
    giftEnabled: boolean; giftNote: string | null;
  };
  hours: { weekday: number; opensAt: number; closesAt: number }[];
  stations: StationRow[];
};

const MODE_HELP: Record<StationMode, string> = {
  PRINTER: 'A comanda sai na impressora do setor e o pedido aparece para o cliente como “enviado para o preparo”. Sem tela, sem etapas de preparo.',
  SCREEN: 'A equipe acompanha numa tela (tablet/TV) e marca em preparo → pronto → entregue. Nada é impresso.',
  BOTH: 'Imprime a comanda e também mostra na tela, onde a equipe marca as etapas.',
};

/** Setores de produção: cada produto aponta para um; cada setor imprime, usa tela, ou os dois. */
function StationsCard({ stations, canEdit }: { stations: StationRow[]; canEdit: boolean }) {
  const [rows, setRows] = useState<(StationRow | (Omit<StationRow, 'id'> & { id?: undefined }))[]>(stations);
  const { busy, save } = useSave();
  useEffect(() => setRows(stations), [stations]);
  const saveRow = (r: (typeof rows)[number]) => {
    const body = { name: r.name, kind: r.kind, defaultPrepMinutes: r.defaultPrepMinutes, mode: r.mode };
    return save(() => (r.id ? patch(`/api/staff/stations/${r.id}`, body) : post('/api/staff/stations', body)), r.id ? 'Setor atualizado' : 'Setor criado');
  };
  return (
    <Card>
      <CardHeader title="Setores de produção" description="Para onde vão as comandas e como cada setor as recebe: impressora, tela ou os dois. O tempo previsto destaca atrasos na tela." />
      <ul className="divide-y divide-line">
        {rows.map((r, i) => (
          <li key={r.id ?? `new-${i}`} className="flex flex-wrap items-end gap-2 px-5 py-3">
            <Field label="Nome" className="min-w-40 flex-1">{(id) => <Input id={id} disabled={!canEdit} value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />}</Field>
            <Field label="Tipo" className="w-36">
              {(id) => (
                <select id={id} disabled={!canEdit} value={r.kind} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, kind: e.target.value as StationRow['kind'] } : x)))} className="h-11 w-full rounded-xl border border-line bg-white px-3 text-[15px]">
                  <option value="KITCHEN">Cozinha</option><option value="BAR">Bar</option><option value="OTHER">Outro</option>
                </select>
              )}
            </Field>
            <Field label="Recebe os pedidos" className="w-48">
              {(id) => (
                <select id={id} disabled={!canEdit} value={r.mode} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, mode: e.target.value as StationMode } : x)))} className="h-11 w-full rounded-xl border border-line bg-white px-3 text-[15px]">
                  {STATION_MODES.map((m) => <option key={m} value={m}>{STATION_MODE_LABEL[m]}</option>)}
                </select>
              )}
            </Field>
            <Field label="Previsto (min)" className="w-32">{(id) => <Input id={id} type="number" min={1} max={240} disabled={!canEdit} value={r.defaultPrepMinutes} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, defaultPrepMinutes: Number(e.target.value) } : x)))} />}</Field>
            {canEdit && <Button variant="secondary" loading={busy} onClick={() => saveRow(r)}>Salvar</Button>}
            {canEdit && r.id && rows.length > 1 && (
              <Button variant="ghost" aria-label={`Arquivar ${r.name}`} icon={<Trash2 className="size-4" />} onClick={() => confirm(`Arquivar o setor "${r.name}"?`) && save(() => post(`/api/staff/stations/${r.id}/archive`), 'Setor arquivado')} />
            )}
            <p className="w-full text-xs text-ink-3">{MODE_HELP[r.mode]}</p>
            {r.mode !== 'SCREEN' && r.printers === 0 && (
              <div className="w-full">
                <Alert tone={r.mode === 'PRINTER' ? 'danger' : 'warning'}>
                  {r.mode === 'PRINTER'
                    ? 'Este setor ainda não tem impressora: os pedidos dele não chegam a ninguém. Configure em Impressão (ou use a tela até lá).'
                    : 'Este setor ainda não tem impressora; por enquanto só a tela recebe os pedidos.'}
                </Alert>
              </div>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="border-t border-line px-5 py-3">
          <Button variant="secondary" size="sm" icon={<Plus className="size-4" />} onClick={() => setRows([...rows, { name: '', kind: 'OTHER', defaultPrepMinutes: 15, mode: 'PRINTER', printers: 0 }])}>Novo setor</Button>
        </div>
      )}
    </Card>
  );
}

export function StaffSettings({ permissions }: { permissions: Permission[] }) {
  const canEdit = permissions.includes('restaurant.settings.edit');
  const q = useQuery({ queryKey: ['staff-settings'], queryFn: () => api<Settings>('/api/staff/settings') });
  return (
    <>
      <PageHeader title="Configurações" description="Funcionamento, pedidos e identidade visual. Dados cadastrais e endereço são alterados pelo suporte do Garlic." />
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.data && (
        <div className="space-y-6">
          {!canEdit && <Alert tone="info">Você pode consultar, mas não alterar, as configurações.</Alert>}
          <OrdersCard data={q.data} canEdit={canEdit} />
          <HoursCard data={q.data} canEdit={canEdit} />
          <BrandingCard data={q.data} canEdit={canEdit} />
          <GiftCard data={q.data} canEdit={canEdit} />
          <StationsCard stations={q.data.stations} canEdit={canEdit} />
        </div>
      )}
    </>
  );
}

function useSave() {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const save = async (fn: () => Promise<unknown>, msg: string, onError?: (e: unknown) => void) => {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['staff-settings'] });
      await qc.invalidateQueries({ queryKey: ['public-menu'] });
      toast.success(msg);
    } catch (e) {
      onError?.(e);
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, save };
}

function OrdersCard({ data, canEdit }: { data: Settings; canEdit: boolean }) {
  const s = data.settings;
  const [f, setF] = useState({ acceptOrders: s.acceptOrders, enforceHours: s.enforceHours, autoOpenSessions: s.autoOpenSessions, menuNotice: s.menuNotice ?? '' });
  const { busy, save } = useSave();
  const rows = [
    ['acceptOrders', 'Aceitar pedidos pelo QR Code', 'Desligue para pausar pedidos pelo celular (ex.: cozinha sobrecarregada). Garçons continuam lançando.'],
    ['enforceHours', 'Recusar pedidos fora do horário', 'Usa os horários de funcionamento abaixo.'],
    ['autoOpenSessions', 'Abrir a mesa automaticamente no primeiro scan', 'Se desligado, um funcionário precisa abrir a comanda antes do cliente pedir.'],
  ] as const;
  return (
    <Card>
      <CardHeader title="Pedidos" />
      <div className="divide-y divide-line">
        {rows.map(([k, l, d]) => (
          <label key={k} className="flex items-center justify-between gap-4 px-5 py-3">
            <span><span className="block text-sm font-medium">{l}</span><span className="block text-xs text-ink-3">{d}</span></span>
            <Switch checked={f[k]} disabled={!canEdit} onChange={(v) => setF({ ...f, [k]: v })} label={l} />
          </label>
        ))}
        <div className="px-5 py-3">
          <Field label="Aviso no topo do cardápio (opcional)" hint="Ex.: Cozinha fecha às 23h. Até 160 caracteres.">
            {(id, d) => <Textarea id={id} aria-describedby={d} maxLength={160} className="min-h-16" disabled={!canEdit} value={f.menuNotice} onChange={(e) => setF({ ...f, menuNotice: e.target.value })} />}
          </Field>
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button loading={busy} icon={<Save className="size-4" />} onClick={() => save(() => put('/api/staff/settings/orders', f), 'Preferências de pedidos salvas')}>Salvar</Button>
        </div>
      )}
    </Card>
  );
}

/** Brinde físico no comprovante de saída, com o convite para avaliar no Google (textos em GIFT_COPY). */
function GiftCard({ data, canEdit }: { data: Settings; canEdit: boolean }) {
  const [f, setF] = useState({ giftEnabled: data.settings.giftEnabled, giftNote: data.settings.giftNote ?? '' });
  const { busy, save } = useSave();
  return (
    <Card>
      <CardHeader title="Brinde na saída" description="Aparece no comprovante de saída como “VOCÊ GANHOU UM BRINDE! NOS AVALIE NO GOOGLE E MOSTRE NA SAÍDA!”, com o botão AVALIAR. Sem link do Google configurado, aparece só o brinde." />
      <div className="space-y-4 p-5">
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium">Oferecer brinde no comprovante de saída</span>
          <Switch checked={f.giftEnabled} disabled={!canEdit} onChange={(giftEnabled) => setF({ ...f, giftEnabled })} label="Oferecer brinde" />
        </label>
        <Field label="O que entregar (visível só para a equipe)" hint="Ex.: café expresso, bala de menta. Aparece na tela do balconista.">
          {(id, d) => <Input id={id} aria-describedby={d} maxLength={80} disabled={!canEdit || !f.giftEnabled} value={f.giftNote} onChange={(e) => setF({ ...f, giftNote: e.target.value })} />}
        </Field>
        {!data.settings.googleReviewUrl && <Alert tone="info">Sem link de avaliação do Google configurado (em Identidade visual), o comprovante mostra o brinde sem o botão de avaliação.</Alert>}
      </div>
      {canEdit && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button loading={busy} icon={<Save className="size-4" />} onClick={() => save(() => put('/api/staff/settings/exit', f), 'Brinde salvo')}>Salvar</Button>
        </div>
      )}
    </Card>
  );
}

type Range = { weekday: number; opens: string; closes: string };

function HoursCard({ data, canEdit }: { data: Settings; canEdit: boolean }) {
  const [ranges, setRanges] = useState<Range[]>(() => data.hours.map((h) => ({ weekday: h.weekday, opens: minutesToHHMM(h.opensAt), closes: minutesToHHMM(h.closesAt) })));
  const [error, setError] = useState<string>();
  const { busy, save } = useSave();
  useEffect(() => setError(undefined), [ranges]);
  const submit = () => {
    const hours = ranges.map((r) => ({ weekday: r.weekday, opensAt: hhmmToMinutes(r.opens), closesAt: hhmmToMinutes(r.closes) }));
    if (hours.some((h) => h.opensAt === null || h.closesAt === null)) return setError('Horário inválido. Use HH:MM.');
    void save(() => put('/api/staff/settings/hours', { hours }), 'Horários salvos');
  };
  const copyToAll = (from: number) => {
    const base = ranges.filter((r) => r.weekday === from);
    setRanges(WEEKDAYS.flatMap((_, d) => base.map((b) => ({ ...b, weekday: d }))));
  };
  return (
    <Card>
      <CardHeader title="Horário de funcionamento" description="No fuso do restaurante. Para fechar depois da meia-noite, use por exemplo 18:00 às 02:00." />
      {error && <div className="px-5 pt-4"><Alert tone="danger">{error}</Alert></div>}
      <ul className="divide-y divide-line">
        {WEEKDAYS.map((day, d) => {
          const dayRanges = ranges.map((r, i) => ({ r, i })).filter(({ r }) => r.weekday === d);
          return (
            <li key={day} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-start">
              <span className="w-24 shrink-0 pt-2 text-sm font-medium">{day}</span>
              <div className="flex-1 space-y-2">
                {dayRanges.length === 0 && <p className="pt-2 text-sm text-ink-3">Fechado</p>}
                {dayRanges.map(({ r, i }) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" aria-label={`${day}: abre`} className="w-32" disabled={!canEdit} value={r.opens} onChange={(e) => setRanges(ranges.map((x, j) => (j === i ? { ...x, opens: e.target.value } : x)))} />
                    <span className="text-ink-3">às</span>
                    <Input type="time" aria-label={`${day}: fecha`} className="w-32" disabled={!canEdit} value={r.closes} onChange={(e) => setRanges(ranges.map((x, j) => (j === i ? { ...x, closes: e.target.value } : x)))} />
                    {canEdit && <Button variant="ghost" size="sm" aria-label="Remover faixa" icon={<Trash2 className="size-4" />} onClick={() => setRanges(ranges.filter((_, j) => j !== i))} />}
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" icon={<Plus className="size-4" />} onClick={() => setRanges([...ranges, { weekday: d, opens: '11:00', closes: '23:00' }])}>Faixa</Button>
                  {dayRanges.length > 0 && <Button variant="ghost" size="sm" onClick={() => copyToAll(d)}>Copiar p/ todos</Button>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {canEdit && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button loading={busy} icon={<Save className="size-4" />} onClick={submit}>Salvar horários</Button>
        </div>
      )}
    </Card>
  );
}

function BrandingCard({ data, canEdit }: { data: Settings; canEdit: boolean }) {
  const s = data.settings;
  const [f, setF] = useState({ primaryColor: s.primaryColor, accentColor: s.accentColor, googleReviewUrl: s.googleReviewUrl ?? '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const { busy, save } = useSave();
  const toast = useToast();
  const qc = useQueryClient();
  const upload = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    setUploading(true);
    try {
      await api('/api/staff/settings/logo', { method: 'POST', body });
      await qc.invalidateQueries({ queryKey: ['staff-settings'] });
      await qc.invalidateQueries({ queryKey: ['context'] });
      toast.success('Logotipo atualizado');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <Card>
      <CardHeader title="Identidade visual" />
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-[1fr_12rem]">
        <div className="grid gap-4 sm:grid-cols-2">
          {(['primaryColor', 'accentColor'] as const).map((k) => (
            <Field key={k} label={k === 'primaryColor' ? 'Cor principal' : 'Cor de destaque'} error={errors[k]}>
              {(id) => (
                <div className="flex gap-2">
                  <input type="color" aria-label="Seletor de cor" disabled={!canEdit} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 w-14 cursor-pointer rounded-xl border border-line bg-white p-1" />
                  <Input id={id} disabled={!canEdit} className="font-mono uppercase" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
                </div>
              )}
            </Field>
          ))}
          <Field className="sm:col-span-2" label="Link de avaliação no Google" error={errors.googleReviewUrl} hint="O botão AVALIAR do comprovante de saída abre este link. No Perfil da Empresa no Google, use “Pedir avaliações” para copiar o seu.">
            {(id, d) => <Input id={id} aria-describedby={d} type="url" disabled={!canEdit} placeholder="https://g.page/r/…/review" value={f.googleReviewUrl} onChange={(e) => setF({ ...f, googleReviewUrl: e.target.value })} />}
          </Field>
        </div>
        <div className="space-y-2">
          <div className="grid aspect-square place-items-center overflow-hidden rounded-2xl border border-dashed border-line bg-paper">
            {s.logoPath ? <img src={s.logoPath} alt="Logotipo" className="size-full object-contain p-3" /> : <span className="text-xs text-ink-3">Sem logotipo</span>}
          </div>
          {canEdit && (
            <label className={cx('flex h-9 cursor-pointer items-center justify-center gap-2 rounded-xl border border-line bg-white text-sm font-medium hover:bg-paper-2', uploading && 'pointer-events-none opacity-60')}>
              <ImageUp className="size-4" /> {uploading ? 'Enviando…' : 'Trocar logotipo'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            </label>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button
            loading={busy}
            icon={<Save className="size-4" />}
            onClick={() =>
              save(
                async () => {
                  await put('/api/staff/settings/branding', f);
                  await qc.invalidateQueries({ queryKey: ['context'] });
                },
                'Identidade visual salva',
                (e) => setErrors(fieldErrors(e)),
              )
            }
          >
            Salvar
          </Button>
        </div>
      )}
    </Card>
  );
}
